import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Briefcase, Search, TrendingUp } from 'lucide-react';
type Card = {
  num: string;
  Icon: React.ElementType;
  title: string;
  desc: string;
  cta: string;
  href: string;
  external?: boolean;
  highlighted?: boolean;
};
const cards: Card[] = [{
  num: '01',
  Icon: Briefcase,
  title: 'Quiero crear mi empresa',
  desc: 'Ya sé lo que quiero vender y estoy listo(a) para formalizarme en Florida con estructura desde el inicio.',
  cta: 'Ver Plan 1',
  href: '#plan-1'
}, {
  num: '02',
  Icon: Search,
  title: 'Primero quiero claridad',
  desc: 'Tengo una idea pero aún tengo dudas sobre estructura y qué tipo de empresa me conviene más.',
  cta: 'Agendar sesión estratégica',
  href: 'https://wa.me/17863037695?text=Hola%2C%20quiero%20reservar%20una%20sesi%C3%B3n%20estrat%C3%A9gica',
  external: true
}, {
  num: '03',
  Icon: TrendingUp,
  title: 'Quiero salir con todo listo',
  desc: 'Quiero crear mi empresa Y tener presencia digital desde el inicio. Sin perder tiempo ni dinero.',
  cta: 'Ver Plan Full Creation',
  href: '#plan-3',
  highlighted: true
}];
export function ElegeCaminoSection() {
  return <section className="relative overflow-hidden md:!bg-[#FAFAFA]" style={{
    backgroundColor: '#faf8ff',
    padding: '48px 20px'
  }} data-id="element-87">
      {/* Subtle texture (desktop only) */}
      <div className="absolute inset-0 pointer-events-none opacity-[0.04] hidden md:block" style={{
      backgroundImage: 'radial-gradient(circle at 1px 1px, #5a4579 1px, transparent 0)',
      backgroundSize: '32px 32px'
    }} aria-hidden="true" data-id="element-88" />

      {/* Responsive padding for desktop */}
      <style data-id="element-89">{`
        @media (min-width: 769px) {
          .elege-section {
            padding: 100px 24px !important;
          }
        }
      `}</style>

      <div className="elege-section max-w-6xl mx-auto relative" data-id="element-90">
        {/* Header */}
        <div className="text-center mb-7 md:mb-14" data-id="element-91">
          <motion.p initial={{
          opacity: 0,
          y: 10
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="hidden md:block" style={{
          color: '#5a4579',
          fontSize: '11px',
          fontWeight: 700,
          letterSpacing: '2px',
          textTransform: 'uppercase'
        }} data-id="element-92">
            Encuentra tu camino
          </motion.p>
          <motion.h2 initial={{
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.05
        }} className="text-[24px] md:text-[36px]" style={{
          fontWeight: 800,
          color: '#2D1B4E',
          textAlign: 'center',
          marginTop: '8px',
          lineHeight: 1.2,
          marginBottom: '8px'
        }} data-id="element-93">
            ¿En qué etapa estás tú?
          </motion.h2>
          <motion.p initial={{
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.1
        }} className="mx-auto text-[14px] md:text-[16px]" style={{
          color: '#6B7280',
          textAlign: 'center',
          lineHeight: 1.6
        }} data-id="element-94">
            <span className="block md:hidden" style={{
            maxWidth: '300px',
            margin: '0 auto'
          }} data-id="element-95">
              No todos empiezan desde el mismo lugar. Elige tu camino.
            </span>
            <span className="hidden md:block" style={{
            maxWidth: '500px',
            margin: '12px auto 0'
          }} data-id="element-96">
              No todos empiezan desde el mismo lugar. Por eso creamos opciones
              claras para que avances con orden y sin gastar de más.
            </span>
          </motion.p>
        </div>

        {/* Cards — column on mobile, grid on desktop */}
        <div className="flex flex-col md:grid md:grid-cols-3 gap-3 md:gap-5" data-id="element-97">
          {cards.map((card, i) => {
          const isHi = !!card.highlighted;
          const {
            Icon
          } = card;
          return <motion.a key={i} href={card.href} {...card.external ? {
            target: '_blank',
            rel: 'noopener noreferrer'
          } : {}} initial={{
            opacity: 0,
            y: 16
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.08
          }} className={`group block relative overflow-hidden transition-all duration-300
                  flex md:flex-col items-center md:items-stretch
                  ${isHi ? 'elege-card-hi' : 'elege-card-base'}
                `} style={isHi ? {
            background: 'linear-gradient(135deg, #5a4579 0%, #8B6BA8 100%)',
            border: 'none',
            borderRadius: '16px',
            boxShadow: '0 8px 24px rgba(90,69,121,0.25)',
            gap: '16px'
          } : {
            background: '#FFFFFF',
            border: '1px solid rgba(90,69,121,0.1)',
            borderRadius: '16px',
            boxShadow: '0 2px 12px rgba(90,69,121,0.06)',
            gap: '16px'
          }} onMouseEnter={e => {
            const el = e.currentTarget as HTMLAnchorElement;
            el.style.transform = 'translateY(-4px)';
            if (isHi) {
              el.style.boxShadow = '0 16px 40px rgba(90, 69, 121, 0.35)';
            } else {
              el.style.borderColor = 'rgba(90,69,121,0.3)';
              el.style.boxShadow = '0 12px 32px rgba(90,69,121,0.12)';
            }
          }} onMouseLeave={e => {
            const el = e.currentTarget as HTMLAnchorElement;
            el.style.transform = 'translateY(0)';
            if (isHi) {
              el.style.boxShadow = '0 8px 24px rgba(90,69,121,0.25)';
            } else {
              el.style.borderColor = 'rgba(90,69,121,0.1)';
              el.style.boxShadow = '0 2px 12px rgba(90,69,121,0.06)';
            }
          }} data-id="element-98">
                <span style={{
              display: 'contents'
            }} data-id="element-99">
                  {/* Inline responsive padding */}
                  <style data-id="element-100">{`
                    .elege-card-base, .elege-card-hi {
                      padding: 18px 20px;
                    }
                    @media (min-width: 769px) {
                      .elege-card-base, .elege-card-hi {
                        padding: 36px 28px !important;
                      }
                    }
                  `}</style>
                </span>

                {/* Icon container */}
                <div className="flex items-center justify-center flex-shrink-0 elege-icon-wrap" style={isHi ? {
              width: '44px',
              height: '44px',
              minWidth: '44px',
              background: 'rgba(255,255,255,0.15)',
              border: '1px solid rgba(255,255,255,0.2)',
              borderRadius: '12px'
            } : {
              width: '44px',
              height: '44px',
              minWidth: '44px',
              background: 'linear-gradient(135deg, rgba(90,69,121,0.08), rgba(90,69,121,0.04))',
              border: '1px solid rgba(90,69,121,0.1)',
              borderRadius: '12px'
            }} data-id="element-101">
                  <Icon size={20} strokeWidth={1.5} style={{
                color: isHi ? '#FFFFFF' : '#5a4579'
              }} data-id="element-102" />
                </div>

                {/* Inline responsive icon container */}
                <style data-id="element-103">{`
                  @media (min-width: 769px) {
                    .elege-icon-wrap {
                      width: 52px !important;
                      height: 52px !important;
                      min-width: 52px !important;
                      border-radius: 14px !important;
                      margin-bottom: 12px !important;
                    }
                    .elege-icon-wrap svg {
                      width: 22px !important;
                      height: 22px !important;
                    }
                  }
                `}</style>

                {/* Text column */}
                <div className="flex-1 min-w-0" data-id="element-104">
                  <div style={{
                fontSize: '10px',
                fontWeight: 700,
                letterSpacing: '1px',
                color: isHi ? 'rgba(255,255,255,0.4)' : 'rgba(90,69,121,0.4)',
                marginBottom: '2px'
              }} data-id="element-105">
                    {card.num}
                  </div>
                  <h3 className="text-[15px] md:text-[20px]" style={{
                fontWeight: isHi ? 800 : 700,
                color: isHi ? '#FFFFFF' : '#2D1B4E',
                marginBottom: '4px',
                lineHeight: 1.3
              }} data-id="element-106">
                    {card.title}
                  </h3>

                  {/* Description — hidden on mobile, shown on desktop */}
                  <p className="hidden md:block" style={{
                fontSize: '15px',
                color: isHi ? 'rgba(255,255,255,0.85)' : '#6B7280',
                lineHeight: 1.65,
                marginBottom: '24px'
              }} data-id="element-107">
                    {card.desc}
                  </p>

                  {/* CTA */}
                  <span className="inline-flex items-center text-[13px] md:text-[14px]" style={{
                color: isHi ? 'rgba(255,255,255,0.9)' : '#5a4579',
                fontWeight: 600,
                gap: '4px',
                marginTop: '6px'
              }} data-id="element-108">
                    <span data-id="element-109">{card.cta}</span>
                    <ArrowRight className="w-3.5 h-3.5 transition-transform duration-300 group-hover:translate-x-1" data-id="element-110" />
                  </span>
                </div>
              </motion.a>;
        })}
        </div>
      </div>
    </section>;
}